import * as React from "react";
import {
  LoaderWrapper,
  Circle,
  LoaderText,
  StyleLoaderProps,
  LoaderSizeType,
} from "./Loader.style";
import Box from "../../layoutComponents/Box";
import Grid from "../../layoutComponents/Grid";

export interface ILoaderProps extends StyleLoaderProps {
  /**
   * Controls the height/width of <Loader />
   */
  size?: LoaderSizeType;
  /**
   * Helper text for <Loader />
   * @default 'Loading...'
   */
  caption?: string;
  /**
   * Hides <Loader /> for `{delay}`ms.
   */
  delay?: number;
}

/**
 * - Returns the appropriate pixel value for the Loader Box container
 * - dimensions: value used for height and width of Loader's container
 * - captionMargin: top margin for caption mesage
 * @param size
 * @returns { dimensions: number, captionMargin: string }
 */
export const setLoaderContainerDimensions = (
  size: string
): { dimensions: number; captionMargin: string } => {
  switch (size) {
    case "sm":
      return { dimensions: 40, captionMargin: "-25px" };
    case "md":
      return { dimensions: 60, captionMargin: "-45px" };
    case "lg":
      return { dimensions: 80, captionMargin: "-55px" };
    default:
      return { dimensions: 40, captionMargin: "-25px" };
  }
};

// Timeout that handles rendering of <Loader />
export const setIsVisibleAfterDelay = (
  delay: number,
  setIsVisible: React.Dispatch<boolean>
): number => {
  const timeoutId = window.setTimeout(() => {
    setIsVisible(true);
  }, delay);
  return timeoutId;
};
/**
 * Dynamic Loader for Lumos
 *
 * @param {ILoaderProps} {
 *   size = 'sm',
 *   caption = 'Loading...',
 *   delay = 0
 * }
 */
const Loader: React.FC<ILoaderProps> = ({
  size = "sm",
  caption = "Loading...",
  delay = 0,
  ...props
}) => {
  /**
   * This state variable allows the `delay` prop to control the render.
   */
  const [isVisible, setIsVisible] = React.useState(delay === 0);
  /**
   * If there is a valid `delay` value, we will toggle the `isVisible` prop to true after `delay` ms.
   */
  React.useEffect(() => {
    let timeoutId: number | undefined;
    // If there is a valid delay value, trigger setTimeout
    if (delay && delay > 0) {
      timeoutId = setIsVisibleAfterDelay(delay, setIsVisible);
    }
    return () => {
      clearTimeout(timeoutId);
    };
  }, [delay]);

  // We don't want to render anything if isVisible is falsy
  if (!isVisible) return null;

  // Destructure dynamic height/width and margin for caption
  const { dimensions, captionMargin } = setLoaderContainerDimensions(size);
  return (
    <LoaderWrapper {...props}>
      <Box mb={1} width={dimensions} height={dimensions}>
        <Circle size={size}>
          <div></div>
          <div></div>
          <div></div>
          <div></div>
        </Circle>
      </Box>
      <Grid
        mt={captionMargin}
        width={dimensions}
        flexDirection="row"
        justifyContent="center"
      >
        <LoaderText size={size}>{caption}</LoaderText>
      </Grid>
    </LoaderWrapper>
  );
};

export default Loader;
